/**
 * Walking an island: what a move costs, whether it is allowed, and what it
 * shows you.
 *
 * The client never decides an outcome (`AGENTS.md`, server authority), so this
 * is the whole of movement: the server hands in the tiles, the player's state
 * and their abilities, and gets back either a new state or a typed refusal.
 * Nothing here reads a clock or a database, so a move can be replayed exactly.
 *
 * Abilities are read but empty in v1 (Q10.4). Diagonal movement and wider
 * sight are already wired through as data, so buying one later is a row in a
 * table rather than a change to the rules.
 */

import { GAME_CONFIG, MOVEMENT_COST, type Terrain } from './game-config.ts'
import type { LogEvent } from './content/schemas.ts'
import {
  addTile,
  CARDINALS,
  copyTileSet,
  createTileSet,
  DIRECTIONS,
  index,
  inBounds,
  square,
  step,
  type Direction,
  type Position,
  type TileSet,
} from './tiles.ts'

/** Abilities the rules know how to read. None are granted in v1. */
export const ABILITIES = ['diagonal-movement', 'wider-sight'] as const
export type Ability = (typeof ABILITIES)[number]
export type Abilities = ReadonlySet<Ability>

/** The v1 player: no abilities at all. */
export const NO_ABILITIES: Abilities = new Set<Ability>()

/**
 * Which budget a walk is on. The return budget is a second allowance, not the
 * remainder of the first: an unspent rising day does not carry over
 * (`docs/rules.md` section 4). Replay is unlimited (Q1.2).
 */
export const BUDGETS = ['live', 'return', 'replay'] as const
export type Budget = (typeof BUDGETS)[number]

/** Stamina a budget starts with. `null` is unlimited. */
export function startingStamina(budget: Budget): number | null {
  switch (budget) {
    case 'live':
      return GAME_CONFIG.stamina.live
    case 'return':
      return GAME_CONFIG.stamina.return
    case 'replay':
      return null
  }
}

/** One player's position on one island. */
export type WalkState = {
  readonly at: Position
  /** Stamina left, or `null` on replay. */
  readonly stamina: number | null
  /** Tiles this player has seen with their own eyes. Hearsay is kept apart. */
  readonly seen: TileSet
}

export type MoveError =
  /** The direction is not one the player may move in. */
  | 'no-such-direction'
  /** The destination is off the grid. */
  | 'off-the-island'
  /** Water. */
  | 'impassable'
  /** The destination costs more than is left. */
  | 'no-stamina'

export type MoveResult =
  | {
      readonly ok: true
      readonly state: WalkState
      readonly entered: Terrain
      readonly cost: number
      /** Tiles this move showed for the first time. */
      readonly revealed: readonly Position[]
      /** True when the move left nothing to spend. */
      readonly exhausted: boolean
    }
  | { readonly ok: false; readonly error: MoveError }

/** Sight radius from a tile, in Chebyshev distance (Q2.1). */
export function sightRadius(standingOn: Terrain, abilities: Abilities = NO_ABILITIES): number {
  const base = standingOn === 'mountain' ? GAME_CONFIG.sight.fromMountain : GAME_CONFIG.sight.base
  return abilities.has('wider-sight') ? base + 1 : base
}

/** The tiles visible from a position. Sight is not blocked by anything. */
export function visibleTiles(
  at: Position,
  tiles: readonly Terrain[],
  abilities: Abilities = NO_ABILITIES,
): Position[] {
  if (!inBounds(at)) return []
  const standingOn = tiles[index(at)]
  if (standingOn === undefined) return []
  return square(at, sightRadius(standingOn, abilities))
}

/** The directions a player may move in. Diagonals need an ability (Q2.2). */
export function allowedDirections(abilities: Abilities = NO_ABILITIES): readonly Direction[] {
  return abilities.has('diagonal-movement')
    ? (Object.keys(DIRECTIONS) as Direction[])
    : (Object.keys(CARDINALS) as Direction[])
}

/** The state a player is in the moment they land, with the shore already seen. */
export function landOn(
  at: Position,
  tiles: readonly Terrain[],
  budget: Budget,
  abilities: Abilities = NO_ABILITIES,
): WalkState {
  const seen = createTileSet()
  for (const tile of visibleTiles(at, tiles, abilities)) addTile(seen, tile)
  return { at, stamina: startingStamina(budget), seen }
}

/**
 * Take one step. Returns a new state or a typed refusal; the caller never has
 * to guess why a move was rejected, because the log line depends on it.
 */
export function applyMove(
  state: WalkState,
  direction: Direction,
  world: { tiles: readonly Terrain[]; abilities?: Abilities },
): MoveResult {
  const abilities = world.abilities ?? NO_ABILITIES
  if (!allowedDirections(abilities).includes(direction)) {
    return { ok: false, error: 'no-such-direction' }
  }

  const destination = step(state.at, direction)
  if (!inBounds(destination)) return { ok: false, error: 'off-the-island' }

  const entered = world.tiles[index(destination)]
  if (entered === undefined) return { ok: false, error: 'off-the-island' }

  const cost = MOVEMENT_COST[entered]
  if (cost === null) return { ok: false, error: 'impassable' }
  if (state.stamina !== null && cost > state.stamina) return { ok: false, error: 'no-stamina' }

  const seen = copyTileSet(state.seen)
  const revealed: Position[] = []
  for (const tile of visibleTiles(destination, world.tiles, abilities)) {
    if (addTile(seen, tile)) revealed.push(tile)
  }

  const stamina = state.stamina === null ? null : state.stamina - cost
  return {
    ok: true,
    state: { at: destination, stamina, seen },
    entered,
    cost,
    revealed,
    exhausted: stamina === 0,
  }
}

/**
 * Whether there is any step left to take (Q20.2).
 *
 * A day is not over only when stamina reaches zero: a player standing on one
 * point with nothing but forest and mountain around them is just as finished,
 * and every direction they try is refused. Asking the rule itself -- rather
 * than comparing numbers somewhere else -- means the answer cannot drift from
 * what a move actually does, and keeps the arithmetic on this side of the line
 * where the client cannot do it.
 */
export function hasLegalMove(
  state: WalkState,
  world: { tiles: readonly Terrain[]; abilities?: Abilities },
): boolean {
  return allowedDirections(world.abilities ?? NO_ABILITIES).some(
    (direction) => applyMove(state, direction, world).ok,
  )
}

/** The log event a successful step onto this terrain reports (`log-lines.json`). */
export function stepEvent(entered: Terrain): LogEvent {
  switch (entered) {
    case 'sand':
      return 'step-sand'
    case 'grass':
      return 'step-grass'
    case 'forest':
      return 'step-forest'
    case 'mountain':
      return 'step-mountain'
    // A ruin is entered for its fragment, and the water edge is a refusal
    // rather than a step, so both report what happened instead of the ground.
    case 'ruin':
      return 'ruin-found'
    case 'water':
      return 'water-edge'
  }
}

/** The log event a refused move reports. */
export function refusalEvent(error: MoveError): LogEvent {
  switch (error) {
    case 'impassable':
      return 'water-edge'
    case 'no-stamina':
      return 'stamina-exhausted'
    case 'off-the-island':
    case 'no-such-direction':
      // Neither is reachable from the interface; the map has no tile there and
      // no control for it. The shore line is the least surprising thing to say.
      return 'water-edge'
  }
}
